import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type CreateUserParams,
  type DmsUser,
  type Role,
  type UpdateUserParams,
  type Zone,
} from "../../domain/entities/user";
import {
  CreateUserError,
  ListRolesError,
  ListUsersError,
  ListZonesError,
  UpdateUserError,
} from "../../domain/errors/errors";
import { type UsersRepository } from "../../domain/repos/users-repo";
import {
  type RolesResponse,
  type UserResponse,
  type UsersResponse,
  type ZonesResponse,
} from "../../infrastructure/responses/users-responses";

const PAGE_SIZE = 100;

export class UsersRepo implements UsersRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listUsers(): Promise<DmsUser[]> {
    try {
      // The backoffice roster is small; page through to the end so the KPIs and
      // the "pendientes" split see every user (keyset pagination, page_size≤100).
      const users: DmsUser[] = [];
      let token: string | undefined;
      do {
        const query = new URLSearchParams({ page_size: String(PAGE_SIZE) });
        if (token) query.set("page_token", token);
        const response = await this.apiClient.get<UsersResponse>(
          `v1/users?${query.toString()}`,
        );
        users.push(...(response.data?.items ?? []));
        token = response.data?.nextToken || undefined;
      } while (token);
      return users;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListUsersError(message, apiError);
    }
  }

  async listRoles(): Promise<Role[]> {
    try {
      const response = await this.apiClient.get<RolesResponse>("v1/roles");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListRolesError(message, apiError);
    }
  }

  async listZones(): Promise<Zone[]> {
    try {
      const response = await this.apiClient.get<ZonesResponse>("v1/zones?country_code=gt");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListZonesError(message, apiError);
    }
  }

  async createUser(params: CreateUserParams): Promise<DmsUser> {
    try {
      // POST creates the user role-less + active; a role/zone picked in the
      // modal is applied right after via PATCH (the backend has no create-with-
      // role endpoint — E5.1).
      const created = await this.apiClient.post<{ name: string; email: string }, UserResponse>(
        "v1/users",
        { name: params.name, email: params.email },
      );
      const user = created.data;
      if (!user) throw new CreateUserError("Missing user in response");
      // Interim (DMS auth plan): set the password so the user can sign in
      // before Google/Cognito exists. Removed with the interim auth.
      if (params.password) {
        await this.setPassword(user.userId, params.password);
      }
      // Nothing else to apply — the user stays role-less with no phone.
      if (!params.roleId && params.phone === undefined && params.supervisorId === undefined)
        return user;
      return await this.updateUser({
        userId: user.userId,
        ...(params.phone !== undefined ? { phone: params.phone } : {}),
        // Only send role/zone when a role was chosen; clear the zone unless the
        // role carries coverage (empty string clears).
        ...(params.roleId
          ? { roleId: params.roleId, deliveryZoneId: params.deliveryZoneId ?? "" }
          : {}),
        // Supervisor only when one was picked (ambassador); omitted otherwise so a
        // new user stays supervisor-less.
        ...(params.supervisorId !== undefined ? { supervisorId: params.supervisorId } : {}),
      });
    } catch (error) {
      if (error instanceof UpdateUserError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new CreateUserError(message, apiError);
    }
  }

  async updateUser(params: UpdateUserParams): Promise<DmsUser> {
    try {
      // Only send the fields being changed; the interceptor snake_cases them
      // (roleId → role_id). An empty string clears a nullable assignment.
      const body: Record<string, string> = {};
      if (params.phone !== undefined) body.phone = params.phone;
      if (params.roleId !== undefined) body.roleId = params.roleId;
      if (params.deliveryZoneId !== undefined) body.deliveryZoneId = params.deliveryZoneId;
      if (params.supervisorId !== undefined) body.supervisorId = params.supervisorId;
      if (params.status !== undefined) body.status = params.status;
      const response = await this.apiClient.patch<Record<string, string>, UserResponse>(
        `v1/users/${params.userId}`,
        body,
      );
      const user = response.data;
      if (!user) throw new UpdateUserError("Missing user in response");
      return user;
    } catch (error) {
      if (error instanceof UpdateUserError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new UpdateUserError(message, apiError);
    }
  }

  // Interim admin-set password (DMS auth plan): POST /v1/auth/set-password. The
  // request interceptor snake_cases userId → user_id. Remove with the interim
  // auth when Cognito lands.
  private async setPassword(userId: string, password: string): Promise<void> {
    await this.apiClient.post<{ userId: string; password: string }, unknown>(
      "v1/auth/set-password",
      { userId, password },
    );
  }
}
